import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import styled from 'styled-components';
import { Button, ButtonText, CapsModal, CashPayoutModal, Empty, H3, LayoutSidebar, ModalFactory, PaymentReviewSidebar, SectionHeader, Text, UnallocatedFundsModal } from 'components';
import { ParticipantListItem } from 'components/Calculator/ParticipantListItem';
import PaymentAdjustmentsModal from 'components/Calculator/PaymentAdjustmentsModal/PaymentAdjustmentsModal';
import { ISidebarNotification } from 'components/Sidebar/components/Sidebar';
import { MakePaymentNotification } from 'config/const';
import { useFeatureFlags } from 'contexts/featureFlags';
import { lineHeight, theme, toRem } from 'design';
import { HoldbackModal } from 'modals';
import { routeConstants } from 'routes';
import { MPContent } from 'screens/MakePayment/styles';
import { selectAPIRequestErrors } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { selectCalculatorRules } from 'store/CalculatorRules';
import { selectPaymentSetsForSite } from 'store/Collections';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeRole, selectCurrentEmployeeSite, selectCurrentEmployeeType } from 'store/Employers';
import { Payment } from 'store/Payment';
import { selectSiteAccountBalance, selectSitePOSIntegration } from 'store/Site';
import { connectEmployeeToPayee, selectCalculatorChangeLog, selectCalculatorPayees, selectCalculatorPaymentSet, selectCalculatorPOSResults, selectCalculatorResults, updatePayeeHoldback, updatePayeeNote } from 'store/TipCalculator';
import { IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import {
  DisbursementMethod,
  FeatureFlags,
  IApiErrorPayloadWithCode,
  IEmployeePayrollTip,
  ISite,
  ITipCalculatorPayee,
  ITipCalculatorResultWarning,
  ITipCalculatorResultWarningUnallocatedRemainder,
  PermissionType,
  TipCalculatorPayeeErrorType,
  TipCalculatorSafeModeStatus,
} from 'types';
import {
  buildTipCalculatorParticipants,
  buildTipCalculatorPayeeErrors,
  buildTipCalculatorWarnings,
  calculateInstant,
  calculatePayroll,
  calculateTipCalculatorPaymentTotals,
  centsToDollarsDisplay,
  centsToNegativeDollarsDisplay,
  getIncludedPayoutTypes,
  getNonRecipientContributors,
  getRolePolicy,
  hasSitePermission,
  isPayPeriodClosed,
  siteUsesHoldbacks,
  sortAlphaNumerically,
} from 'utils';
import { roleSiteDisplay } from 'utils/employee';
import ConnectEmployeeModal from '../components/modals/ConnectEmployeeModal/ConnectEmployeeModal';
import EmployeeListItem from '../components/Review/EmployeeListItem/EmployeeListItem';
import { ICalculatorPaymentForm } from '../types';

interface IReviewProps {
  formik: FormikProps<ICalculatorPaymentForm>;
  pageHeaderContent: React.ReactNode;
  payrollTips: IEmployeePayrollTip[];
  site: ISite;
  onNext: () => void;
}

const WarningMessage = styled.div`
  display: flex;
  flex-flow: column;

  button {
    margin-top: ${toRem(theme.space.md)};
    color: ${theme.color.primary[500]};
    text-align: left;
    text-decoration: none;

    &:hover {
      cursor: pointer;
      color: ${theme.color.primary[600]};
    }
  }
`;

const WarningList = styled.ul`
  font-size: ${toRem(theme.font.size.xs)};
  list-style: disc inside;
  padding-top: ${toRem(theme.space.lg)};
`;

const WarningListItem = styled.li``;

/* Where the top buttons stop sharing one line and need to wrap */
const BUTTON_WRAP_WIDTH = 1360;

const ButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${toRem(theme.space.xs)};
  width: 100%;

  & > * {
    width: ${toRem(120)};
  }

  @media (max-width: ${BUTTON_WRAP_WIDTH}px) {
    display: grid;
    /* As many even columns as fit. The 30% caps it at three, so six buttons fill two rows and never
       strand one; the 142px is the widest label, "Other Contributors", which must not be pinched. */
    grid-template-columns: repeat(auto-fit, minmax(max(30%, ${toRem(142)}), 1fr));

    & > * {
      width: 100%;
    }
  }
`;

const ReviewSectionHeader = styled(SectionHeader)`
  @media (max-width: ${BUTTON_WRAP_WIDTH}px) {
    > section:last-child {
      width: 100%;
    }
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  gap: ${toRem(theme.space.sm)};
`;

const ChangeLogLink = styled(NavLink)`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.primary[600]};
  text-decoration: none;

  &:hover {
    color: ${theme.color.primary[700]};
  }
`;

const getNegativePaymentNotification = (negativePayees: ITipCalculatorPayee[]) => {
  if (!negativePayees.length) return undefined;
  return {
    ...MakePaymentNotification.tipCalculator.negativeNetPayment,
    message: (
      <WarningMessage>
        <Text size="small">{MakePaymentNotification.tipCalculator.negativeNetPayment.message}</Text>
        <WarningList>
          {negativePayees.map((payee) => (
            <WarningListItem key={payee.id}>
              {payee.name} {centsToDollarsDisplay(payee.employee?.disbursementMethod === DisbursementMethod.INSTANT ? calculateInstant(payee) : calculatePayroll(payee))}
            </WarningListItem>
          ))}
        </WarningList>
      </WarningMessage>
    ),
  };
};

const getIneligiblePayeesNotification = (ineligiblePayees: ITipCalculatorPayee[]) => {
  if (!ineligiblePayees.length) return undefined;
  return {
    ...MakePaymentNotification.tipCalculator.ineligiblePayees,
    message: (
      <WarningMessage>
        <Text size="small">{MakePaymentNotification.tipCalculator.ineligiblePayees.message}</Text>
        <WarningList>
          {ineligiblePayees.map((payee) => (
            <WarningListItem key={payee.id}>
              {payee.employee.name} - {roleSiteDisplay(payee.employee.role)}
            </WarningListItem>
          ))}
        </WarningList>
      </WarningMessage>
    ),
  };
};

/**
 * The adjustments link was internal-only, shown to Kickfin remote support sessions. The flag opens it to
 * every calculator user; remote sessions keep it either way, so turning the flag back off restores
 * today's behavior rather than taking the link away from support.
 */
const getAlreadyPaidNotification = (canViewAdjustments: boolean, showAdjustmentModal: () => void): ISidebarNotification => ({
  ...MakePaymentNotification.tipCalculator.dateAlreadyPaid,
  message: (
    <WarningMessage>
      <Text size="small">{MakePaymentNotification.tipCalculator.dateAlreadyPaid.message}</Text>
      {canViewAdjustments && <ButtonText label="View Changes" onClick={showAdjustmentModal} />}
    </WarningMessage>
  ),
});

const getCappedWarningNotification = (cappedWarnings: ITipCalculatorResultWarning[], showCapWarningModal: () => void): ISidebarNotification | undefined => {
  if (!cappedWarnings.length) return undefined;
  return {
    ...MakePaymentNotification.tipCalculator.cappedWarnings,
    message: (
      <WarningMessage>
        <Text size="small">{MakePaymentNotification.tipCalculator.cappedWarnings.message}</Text>
        <ButtonText label="View Details" onClick={showCapWarningModal} />
      </WarningMessage>
    ),
  };
};

const getUnallocatedFundsNotification = (unallocatedFundsWarnings: ITipCalculatorResultWarningUnallocatedRemainder[], showUnallocatedFundsWarningModal: () => void): ISidebarNotification | undefined => {
  if (!unallocatedFundsWarnings.length) return undefined;
  const totalUnallocated = unallocatedFundsWarnings.reduce((sum, warning) => sum + (warning.remainder?.unallocated?.total ?? 0), 0);
  return {
    ...MakePaymentNotification.tipCalculator.unallocatedFundsWarnings,
    message: (
      <WarningMessage>
        <Text size="small">{centsToDollarsDisplay(totalUnallocated)} of tips collected cannot be disbursed. If you proceed, these tips will not be paid by Kickfin.</Text>
        <ButtonText label="View Details" onClick={showUnallocatedFundsWarningModal} />
      </WarningMessage>
    ),
  };
};

const getOverdraftNotification = (totals: { instant: number; holdbacks: number }, siteBalance: number | null, overdraftLimit: number) => {
  const netInstant = totals.instant - totals.holdbacks;
  if (netInstant <= 0) return undefined;

  const balance = siteBalance ?? 0;

  if (netInstant > balance + overdraftLimit) {
    return {
      ...MakePaymentNotification.overdraftWarning,
      message: `Your new balance is below the limit of ${centsToNegativeDollarsDisplay(overdraftLimit, true)}. Payment cannot be accepted.`,
      preventSubmit: true,
    };
  }

  if (netInstant > balance) {
    return {
      ...MakePaymentNotification.overdraftWarning,
      message: `Your new balance will be ${centsToNegativeDollarsDisplay(netInstant - balance, true)}. Your limit is ${centsToNegativeDollarsDisplay(overdraftLimit, true)}.`,
      preventSubmit: false,
    };
  }

  return undefined;
};

const dataCyBase = 'review-list';
const apiErrorRegex = /(\d+) net/g;

const getSystemErrors = (apiErrors: IApiErrorPayloadWithCode) =>
  apiErrors?.errorMessages?.map((rawError: string) => {
    if (rawError.startsWith('payment_date')) return MakePaymentNotification.tipCalculator.dateAlreadyPaid;

    // parses "<value> net amount exceeds the site per tip maximum amount" error received from api
    const errorMsg = rawError.replace(apiErrorRegex, (_match, cents) => {
      const amount = centsToDollarsDisplay(parseInt(cents, 10));
      return `${amount} net`;
    });

    return { ...MakePaymentNotification.systemError, message: errorMsg };
  }) || [];

const groupPayeesByError = (payees: ITipCalculatorPayee[]): Map<TipCalculatorPayeeErrorType, ITipCalculatorPayee[]> => {
  const errorPayees = new Map<TipCalculatorPayeeErrorType, ITipCalculatorPayee[]>();
  payees.forEach((payee) => {
    payee.errors?.forEach((error) => {
      const existing = errorPayees.get(error.type) ?? [];
      existing.push(payee);
      errorPayees.set(error.type, existing);
    });
  });
  return errorPayees;
};

export const Review: React.FC<IReviewProps> = ({ formik, onNext, pageHeaderContent, payrollTips, site }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const integration = useSelector(selectSitePOSIntegration());
  const currentUser = useSelector(selectCurrentUser());
  const adjustmentsVisibleToAll = useFeatureFlags()[FeatureFlags.CALCULATOR_ORIGINAL_SETTINGS]?.enabled ?? false;
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployeeSite = useSelector(selectCurrentEmployeeSite);
  const currentEmployeeOrg = useSelector(selectCurrentEmployeeOrg);
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  const siteBalance = useSelector(selectSiteAccountBalance());
  const rawPayees = useSelector(selectCalculatorPayees()) || [];
  const tipCalcResults = useSelector(selectCalculatorResults()) || [];
  const currentCalculatorRules = useSelector(selectCalculatorRules());
  const posResults = useSelector(selectCalculatorPOSResults());
  const paymentSet = useSelector(selectCalculatorPaymentSet());
  const paymentSetsForCalendarDates = useSelector(selectPaymentSetsForSite());
  const changeLogEntries = useSelector(selectCalculatorChangeLog());
  const apiSubmitErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, Payment.CREATE_PAYMENT_REQUEST)) as IApiErrorPayloadWithCode;

  const {
    cashPayments: cashPaymentsEnabled,
    createPayments: createPaymentsEnabled,
    holdback: currentSiteHoldbacks,
    tipCalculatorPercentFee: currentSiteTipCalcPercentFee,
    overdraftLimit = 0,
    perTipMax = 0,
    rolePolicy,
    tipCalculatorSafeMode,
  } = site.settings || {};
  const currentEmployeePolicy = getRolePolicy(rolePolicy, currentEmployeeType, currentEmployeeRole);
  const usesHoldbacks = siteUsesHoldbacks(currentSiteHoldbacks);

  const payees = useMemo(() => rawPayees.map((payee) => buildTipCalculatorPayeeErrors(payee, perTipMax, rolePolicy, currentEmployee)).sort(sortAlphaNumerically('name')), [rawPayees, perTipMax, rolePolicy, currentEmployee]);
  const participants = useMemo(() => buildTipCalculatorParticipants(tipCalcResults).sort(sortAlphaNumerically('name')), [tipCalcResults]);
  const includedPayouts = useMemo(() => getIncludedPayoutTypes(payees, usesHoldbacks), [payees, usesHoldbacks]);
  const totals = useMemo(() => calculateTipCalculatorPaymentTotals(payees), [payees]);
  const hasOtherContributors = useMemo(() => getNonRecipientContributors(posResults.data, tipCalcResults).length > 0, [posResults.data, tipCalcResults]);
  const { cappedWarnings, unallocatedFundsWarnings } = useMemo(() => buildTipCalculatorWarnings(tipCalcResults), [tipCalcResults]);
  const originalPayment = paymentSetsForCalendarDates?.find((item) => item.paymentDate === paymentSet?.date);

  useEffect(() => {
    if (!paymentSet) history.push(routeConstants.private.makePayment);
  }, [paymentSet, history]);

  const [showCashPayoutModal, hideCashPayoutModal] = useModal(() => <CashPayoutModal dataCy="cash-payout-modal" payees={payees} hideModal={hideCashPayoutModal} />, [payees]);

  const [currentHoldbackPayee, setCurrentHoldbackPayee] = useState<ITipCalculatorPayee>();
  const [showHoldbackModal, hideHoldbackModal] = useModal(
    () =>
      currentHoldbackPayee && (
        <HoldbackModal
          defaultValue={currentHoldbackPayee.holdbackAmount}
          hideModal={hideHoldbackModal}
          onChange={(holdback) => dispatch(updatePayeeHoldback(currentHoldbackPayee, holdback))}
          payee={currentHoldbackPayee}
          disabled={!currentEmployeePolicy?.holdbackAdjustment}
        />
      ),
    [currentHoldbackPayee, currentEmployeePolicy]
  );

  const [currentConnectPayee, setCurrentConnectPayee] = useState<ITipCalculatorPayee>();
  const [showConnectEmployeeModal, hideConnectEmployeeModal] = useModal(
    () =>
      currentConnectPayee && (
        <ModalFactory customModal onClose={hideConnectEmployeeModal} isActive closeOnOutSideClick={false}>
          <ConnectEmployeeModal
            onClose={hideConnectEmployeeModal}
            provider={currentConnectPayee.provider}
            integration={integration}
            site={currentEmployeeSite}
            organization={currentEmployeeOrg}
            onSuccess={(employee) => dispatch(connectEmployeeToPayee(employee, site, payrollTips))}
          />
        </ModalFactory>
      ),
    [currentConnectPayee, integration, site, payrollTips, currentEmployeeSite, currentEmployeeOrg]
  );

  const [showCapWarningModal, hideCapWarningModal] = useModal(() => <CapsModal warnings={cappedWarnings} hideModal={hideCapWarningModal} />, [cappedWarnings]);

  const [showUnallocatedFundsWarningModal, hideUnallocatedFundsWarningModal] = useModal(
    () => <UnallocatedFundsModal warnings={unallocatedFundsWarnings} tipCalcResults={tipCalcResults} hideModal={hideUnallocatedFundsWarningModal} />,
    [unallocatedFundsWarnings, tipCalcResults]
  );

  const [showAdjustmentModal, hideAdjustmentModal] = useModal(
    () => <PaymentAdjustmentsModal originalPayment={originalPayment} currentPayees={payees} currentParticipants={participants} hideModal={hideAdjustmentModal} />,
    [originalPayment, payees, participants]
  );

  const onCancel = () => history.push(routeConstants.private.makePayment);

  const onSubmit = () => {
    if (payees.some((payee) => !payee.employee)) return;
    formik.setFieldValue('payees', payees);
    onNext();
  };

  const handleNoteChange = useCallback((payeeId: string, note: string) => dispatch(updatePayeeNote(payeeId, note)), [dispatch]);

  const onEditHoldback = useCallback(
    (payee: ITipCalculatorPayee) => {
      setCurrentHoldbackPayee(payee);
      showHoldbackModal();
    },
    [showHoldbackModal]
  );

  const onConnectEmployee = useCallback(
    (payee: ITipCalculatorPayee) => {
      setCurrentConnectPayee(payee);
      showConnectEmployeeModal();
    },
    [showConnectEmployeeModal]
  );

  const primaryNotification = useMemo((): ISidebarNotification => {
    const errors = [
      {
        // Date Already Paid: Prevents the submission of the payment if the date has already been paid.
        condition: () => !!originalPayment,
        notification: () => getAlreadyPaidNotification(adjustmentsVisibleToAll || currentUser?.remoteSession, showAdjustmentModal),
      },
      {
        // Closed Pay Period: Prevents the submission of the payment until an Org Admin reopens the period.
        condition: () => isPayPeriodClosed(paymentSet?.payPeriod),
        notification: () => MakePaymentNotification.payPeriodClosed,
      },
      {
        // Safe Mode: Prevents the submission of the payment while the tip calculator is in safe mode.
        condition: () => tipCalculatorSafeMode === TipCalculatorSafeModeStatus.ENABLED,
        notification: () => MakePaymentNotification.tipCalculator.safeMode,
      },
      {
        // Payments Disabled: Prevents the submission of the payment if payments are disabled.
        condition: () => !createPaymentsEnabled,
        notification: () => MakePaymentNotification.paymentsDisabled,
      },
      {
        // Has Payment Create Permission: Prevents the submission of the payment if the user does not have permission to create payments.
        condition: () => !hasSitePermission(currentEmployeeSite?.permissions, PermissionType.PAYMENT_SUBMIT),
        notification: () => MakePaymentNotification.permissionDenied,
      },
    ];

    return errors.find((err) => err.condition())?.notification();
  }, [originalPayment, currentUser, adjustmentsVisibleToAll, showAdjustmentModal, paymentSet?.payPeriod, tipCalculatorSafeMode, createPaymentsEnabled, currentEmployeeSite]);

  const secondaryNotifications = useMemo((): ISidebarNotification[] => {
    const errorPayees = groupPayeesByError(payees);

    const errors = [
      {
        // No Payees: Prevents submission if there are no payees.
        condition: () => payees.length === 0,
        notification: () => MakePaymentNotification.tipCalculator.noPayees,
      },
      {
        // Overdraft: Warns or prevents submission based on overdraft status.
        condition: () => true, // is always calculated
        notification: () => getOverdraftNotification(totals, siteBalance, overdraftLimit),
      },
      {
        // User Setup Not Complete: Prevents the submission of the payment until the payees are setup successfully.
        condition: () => errorPayees.has(TipCalculatorPayeeErrorType.USER_SETUP_NOT_COMPLETE),
        notification: () => MakePaymentNotification.tipCalculator.userSetupNotComplete,
      },
      {
        // Negative Payment: Prevents the submission of the payment until the payees are adjusted to resolve the negative payment.
        condition: () => errorPayees.has(TipCalculatorPayeeErrorType.NEGATIVE_NET_PAYMENT),
        notification: () => getNegativePaymentNotification(errorPayees.get(TipCalculatorPayeeErrorType.NEGATIVE_NET_PAYMENT)),
      },
      {
        // Max Tip Amount Exceeded: Prevents the submission of the payment if the payees have a payment amount that exceeds the site's maximum payment amount.
        condition: () => errorPayees.has(TipCalculatorPayeeErrorType.MAX_PAYMENT_EXCEEDED),
        notification: () => MakePaymentNotification.tipCalculator.paymentAmountExceeded,
      },
      {
        // Ineligible Payees: Prevents the submission of the payment if the payees are ineligible to receive tips.
        condition: () => errorPayees.has(TipCalculatorPayeeErrorType.INELIGIBLE_PAYABLE),
        notification: () => getIneligiblePayeesNotification(errorPayees.get(TipCalculatorPayeeErrorType.INELIGIBLE_PAYABLE)),
      },
      {
        // Ineligible Self Pay: Prevents the submission of the payment if the current user is ineligible to receive tips.
        condition: () => errorPayees.has(TipCalculatorPayeeErrorType.INELIGIBLE_SELF_PAY),
        notification: () => MakePaymentNotification.tipCalculator.ineligibleSelfPay,
      },
      {
        // Capped Warning: Warns if there are capped warnings in the calculator results.
        condition: () => cappedWarnings.length > 0,
        notification: () => getCappedWarningNotification(cappedWarnings, showCapWarningModal),
      },
      {
        // Unallocated Funds Warning: Warns if there are unallocated funds in the calculator results.
        condition: () => unallocatedFundsWarnings.length > 0,
        notification: () => getUnallocatedFundsNotification(unallocatedFundsWarnings, showUnallocatedFundsWarningModal),
      },
    ];

    return errors.map((err) => err.condition() && err.notification());
  }, [payees, totals, siteBalance, overdraftLimit, cappedWarnings, unallocatedFundsWarnings]);

  const sidebarNotifications = useMemo((): ISidebarNotification[] => {
    const systemErrors = getSystemErrors(apiSubmitErrors);
    return [...systemErrors, primaryNotification, ...secondaryNotifications].filter(Boolean);
  }, [apiSubmitErrors, primaryNotification, secondaryNotifications]);

  return (
    <LayoutSidebar
      content={
        <>
          {pageHeaderContent}

          <MPContent data-cy={dataCyBase}>
            <ReviewSectionHeader
              data-cy={`${dataCyBase}-header`}
              title={
                <TitleWrapper>
                  <H3>Payees ({payees.length + participants.length})</H3>
                  {changeLogEntries.length > 0 && (
                    <ChangeLogLink to={routeConstants.private.makePaymentCalculatorChangeLog} data-cy="view-change-log-button">
                      View Changes
                    </ChangeLogLink>
                  )}
                </TitleWrapper>
              }
            >
              <ButtonsContainer>
                {cashPaymentsEnabled && <Button appearance="white" size="small" data-cy={`${dataCyBase}-cash-payment-button-link`} label="Cash Payouts" onClick={showCashPayoutModal} />}
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-pools-button-link`} label="Pools" to={routeConstants.private.makePaymentCalculatorPools} />
                {hasOtherContributors && <Button appearance="white" size="small" data-cy={`${dataCyBase}-other-contributors-button-link`} label="Other Contributors" to={routeConstants.private.makePaymentCalculatorOtherContributors} />}
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-checks-button-link`} label="Checks" to={routeConstants.private.makePaymentCalculatorChecks} />
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-labor-data-button-link`} label="Labor Data" to={routeConstants.private.makePaymentCalculatorLaborData} />
                <Button appearance="white" size="small" data-cy={`${dataCyBase}-tip-data-button-link`} label="Tip Data" to={routeConstants.private.makePaymentCalculatorTipAmounts} />
              </ButtonsContainer>
            </ReviewSectionHeader>

            {participants.map((participant) => (
              <ParticipantListItem key={`participant_${participant.name}`} participant={participant} calculatorRules={currentCalculatorRules} />
            ))}

            {payees.length === 0 ? (
              <Empty container containerHeight={250} icon="People" message="No Payees" />
            ) : (
              payees.map((payee) => (
                <EmployeeListItem
                  key={`payee-${payee.id}`}
                  dataCy={`${dataCyBase}-item`}
                  currentCalculatorRules={currentCalculatorRules}
                  includedPayouts={includedPayouts}
                  maxTipAmount={perTipMax}
                  onCashEdit={showCashPayoutModal}
                  onConnectEmployee={onConnectEmployee}
                  onEditHoldback={onEditHoldback}
                  onNoteChange={handleNoteChange}
                  creditCardFee={currentSiteTipCalcPercentFee}
                  currentSite={currentEmployeeSite}
                  payee={payee}
                />
              ))
            )}
          </MPContent>
        </>
      }
      sidebar={
        <PaymentReviewSidebar
          businessDate={dayjs(paymentSet?.date)}
          onBack={onCancel}
          onContinue={onSubmit}
          showCash={totals.cash > 0}
          showHoldbacks={usesHoldbacks}
          totals={totals}
          notifications={sidebarNotifications}
          participants={participants}
          isTipCalculator
        />
      }
    />
  );
};
